import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProjectFileNode } from "../models/shared/ProjectFileNode";
import {
    SharePointDriveLocation,
    SharePointFolderItem,
    SharePointFolderPage,
    toSharePointNode,
} from "../models/projects/SharePointFolderItem";
import { SharePointSortOption } from "../services/SharePoint/SharePointGraphService";
import { Log } from "../services/Logger";
import { ActiveSharePointConnection, ProjectFilesErrorKind, toSharePointErrorKind } from "./useSharePointRootFolders";

export interface UseSharePointFolderBrowsingParams {
    /** Null when the user is not inside a SharePoint root; the hook is idle. */
    active: ActiveSharePointConnection | null;
    sortBy: SharePointSortOption;
    onError?: (kind: ProjectFilesErrorKind) => void;
}

export interface UseSharePointFolderBrowsingResult {
    /** Items of the current folder as browser nodes (read-only; no selection yet). */
    nodes: ProjectFileNode[];
    /** Connection name at the root, otherwise the current folder's name. */
    currentPath: string;
    /** True once the user is at least one folder below the connection root. */
    canGoBack: boolean;
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMoreItems: boolean;
    hasPaginated: boolean;
    loadError: boolean;
    enterFolder: (node: ProjectFileNode) => void;
    /** Pops one level; returns false when already at the root so the caller can exit SharePoint. */
    goBack: () => boolean;
    loadNextPage: () => void;
    retry: () => void;
}

interface FolderFrame {
    location: SharePointDriveLocation;
    name: string;
}

const EMPTY: UseSharePointFolderBrowsingResult = {
    nodes: [],
    currentPath: "",
    canGoBack: false,
    isLoading: false,
    isLoadingMore: false,
    hasMoreItems: false,
    hasPaginated: false,
    loadError: false,
    enterFolder: () => undefined,
    goBack: () => false,
    loadNextPage: () => undefined,
    retry: () => undefined,
};

const cacheKey = (location: SharePointDriveLocation, sort: SharePointSortOption) =>
    `${location.driveId}:${location.itemId ?? "root"}:${sort}`;

/**
 * Navigation, listing and paging for one SharePoint root, backed by Graph. Sibling of
 * `useProjectFolderSelection` (NPC); the browser swaps between them by data source only, so the
 * NPC hook is untouched. Pages are cached per folder+sort for the life of the active connection,
 * and a request counter discards responses that arrive after the user has moved on.
 */
export function useSharePointFolderBrowsing(
    params: UseSharePointFolderBrowsingParams
): UseSharePointFolderBrowsingResult {
    const { active, sortBy, onError } = params;
    const [stack, setStack] = useState<FolderFrame[]>([]);
    const [items, setItems] = useState<SharePointFolderItem[]>([]);
    const [nextLink, setNextLink] = useState<string | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasPaginated, setHasPaginated] = useState(false);
    const [loadError, setLoadError] = useState(false);

    const requestIdRef = useRef(0);
    const pageCacheRef = useRef(new Map<string, SharePointFolderPage>());
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const nextLinkRef = useRef(nextLink);
    nextLinkRef.current = nextLink;
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const current = stack.length > 0 ? stack[stack.length - 1] : null;
    const connectionId = active?.connection.id;

    const loadFolder = useCallback(
        async (location: SharePointDriveLocation) => {
            if (!active) return;
            const requestId = ++requestIdRef.current;
            setLoadError(false);
            setIsLoadingMore(false);
            setHasPaginated(false);

            const cached = pageCacheRef.current.get(cacheKey(location, sortBy));
            if (cached) {
                setItems(cached.items);
                setNextLink(cached.nextLink);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const page = await active.graphService.getFolderChildrenPage(location, sortBy);
                if (requestId !== requestIdRef.current) return;
                pageCacheRef.current.set(cacheKey(location, sortBy), page);
                setItems(page.items);
                setNextLink(page.nextLink);
            } catch (error) {
                if (requestId !== requestIdRef.current) return;
                Log.error("useSharePointFolderBrowsing: failed to load SharePoint folder", error);
                setItems([]);
                setNextLink(undefined);
                setLoadError(true);
                onErrorRef.current?.(toSharePointErrorKind(error));
            } finally {
                if (requestId === requestIdRef.current) setIsLoading(false);
            }
        },
        [active, sortBy]
    );

    // Entering/leaving a SharePoint root resets navigation and the page cache.
    useEffect(() => {
        requestIdRef.current += 1;
        pageCacheRef.current = new Map();
        setItems([]);
        setNextLink(undefined);
        setLoadError(false);
        setIsLoading(false);
        setIsLoadingMore(false);
        setHasPaginated(false);
        setStack(active ? [{ location: active.root, name: active.connection.name }] : []);
        // Also runs on unmount, so a late response can't touch disposed state or raise a stale error.
        return () => {
            requestIdRef.current += 1;
        };
    }, [connectionId]);

    // (Re)load whenever the current folder or the sort changes.
    useEffect(() => {
        if (!active || !current) return;
        // tslint:disable-next-line:no-floating-promises
        loadFolder(current.location);
    }, [active, current?.location.driveId, current?.location.itemId, sortBy, loadFolder]);

    const itemsByNodeId = useMemo(
        () => new Map(items.map((item) => [toSharePointNode(item, connectionId ?? "").id, item])),
        [items, connectionId]
    );
    const nodes = useMemo(() => items.map((item) => toSharePointNode(item, connectionId ?? "")), [items, connectionId]);

    const enterFolder = useCallback(
        (node: ProjectFileNode) => {
            const item = itemsByNodeId.get(node.id);
            if (!item || item.type !== "folder") return;
            setStack((prev) => [
                ...prev,
                { location: { driveId: item.parentDriveId, itemId: item.id }, name: item.name },
            ]);
        },
        [itemsByNodeId]
    );

    const goBack = useCallback((): boolean => {
        if (stack.length <= 1) return false;
        setStack((prev) => prev.slice(0, -1));
        return true;
    }, [stack.length]);

    const loadNextPage = useCallback(() => {
        const link = nextLinkRef.current;
        if (!active || !current || !link || isLoadingMore) return;
        const requestId = requestIdRef.current;
        setIsLoadingMore(true);
        setLoadError(false);
        active.graphService
            .getFolderChildrenPage(current.location, sortBy, link)
            .then((page) => {
                if (requestId !== requestIdRef.current) return;
                const merged = { items: [...itemsRef.current, ...page.items], nextLink: page.nextLink };
                pageCacheRef.current.set(cacheKey(current.location, sortBy), merged);
                setItems(merged.items);
                setNextLink(merged.nextLink);
                setHasPaginated(true);
            })
            .catch((error) => {
                if (requestId !== requestIdRef.current) return;
                Log.error("useSharePointFolderBrowsing: failed to load next SharePoint page", error);
                setLoadError(true);
                onErrorRef.current?.(toSharePointErrorKind(error));
            })
            .then(() => {
                if (requestId === requestIdRef.current) setIsLoadingMore(false);
            });
    }, [active, current, sortBy, isLoadingMore]);

    const retry = useCallback(() => {
        if (!current) return;
        if (itemsRef.current.length > 0 && nextLinkRef.current) {
            loadNextPage();
            return;
        }
        pageCacheRef.current.delete(cacheKey(current.location, sortBy));
        // tslint:disable-next-line:no-floating-promises
        loadFolder(current.location);
    }, [current, sortBy, loadFolder, loadNextPage]);

    if (!active || !current) return EMPTY;

    return {
        nodes,
        currentPath: current.name,
        canGoBack: stack.length > 1,
        isLoading,
        isLoadingMore,
        hasMoreItems: !!nextLink,
        hasPaginated,
        loadError,
        enterFolder,
        goBack,
        loadNextPage,
        retry,
    };
}
